import { FormEvent } from "react";
import { InputAdornment, Paper, styled, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";

import {
  useInitialValues,
  useMeasureValidationSchema,
} from "hooks/measureHooks";
import { useTranslation } from "react-i18next";

import { Measure, MeasureConf } from "types/types";

import { FormikTextField } from "./FormikTextField";

interface Props {
  measureConfigs: MeasureConf[];
  selectedMeasure: Measure;
  measureConfigid: number;
  onSubmitForm: (form: any) => void;
  onDiameterChanged: (diameter: number) => void;
}

export function MeasureImpactForm(props: Props) {
  const {
    measureConfigs,
    measureConfigid,
    selectedMeasure,
    onSubmitForm,
    onDiameterChanged,
  } = props;
  const { t } = useTranslation();

  const initialValues = useInitialValues(
    selectedMeasure,
    measureConfigid,
    measureConfigs
  );

  const validationSchema = useMeasureValidationSchema(
    measureConfigs,
    measureConfigid
  );

  return (
    <StyledPaper elevation={0}>
      <Typography variant="h4-green" component="h2">
        {t("label.measureProperties")}
      </Typography>
      <Formik
        onSubmit={onSubmitForm}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form id="measure-form">
            {formik.values.measureProperties.map((property, i) => {
              return (
                <Field
                  onChange={(e: FormEvent<HTMLInputElement>) => {
                    formik.setFieldValue(
                      `measureProperties.${i}.value`,
                      e.currentTarget.value
                    );

                    if (
                      property.propertyKey === "diameter" &&
                      selectedMeasure.geometryType === "POINT"
                    ) {
                      const diameter = +e.currentTarget.value;
                      onDiameterChanged(diameter);
                    }
                  }}
                  autoFocus
                  component={FormikTextField}
                  name={`measureProperties.${i}.value`}
                  label={`${property.label}`}
                  type="number"
                  key={property.id}
                  className="flex-field"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {property.unit}
                      </InputAdornment>
                    ),
                  }}
                />
              );
            })}
          </Form>
        )}
      </Formik>
    </StyledPaper>
  );
}

const StyledPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  max-height: 40%;
  margin-right: -${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};

  flex-shrink: 0;

  form {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: flex-start;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    overflow: auto;
    height: 100%;
  }

  .flex-field {
    flex-grow: 1;
    max-width: auto;
  }
`;
